/**
 * Der Reduzierer des Stores. Eine reine Funktion - sie laedt nichts nach und ruft nichts auf, sie
 * ENTSCHEIDET nur. Das Ausfuehren (Nachladen, Abonnieren) liegt in `Speicher.tsx`.
 *
 * Die tragende Entscheidung steht im Fall `sse`. Ein Ereignis traegt keine Nutzdaten, nur
 * `entity`, `id`, `rowVersion`, `actor` (Global Constraint 13) - der Reduzierer kann daraus also
 * gar nichts uebernehmen, selbst wenn er wollte. Er unterscheidet drei Faelle:
 *
 *   1. Ereignis zu einem NICHT geoeffneten Datensatz  -> gezielt nachladen (`nachzuladen`)
 *   2. Ereignis zum GEOEFFNETEN Datensatz             -> Konflikt vormerken, Formular unangetastet
 *   3. `409` beim Speichern                           -> derselbe Konflikt, mit Serverstand
 *
 * Damit entfallen zwei Notloesungen des Bestands:
 *
 *   - `refreshFromServer()` (`index.html:5916`) setzt die Aktualisierung KOMPLETT aus, sobald
 *     irgendetwas geoeffnet ist (`|| openId || openSupplierId || boardEditorDraft || ...`). Wer
 *     einen Drawer offen liegen laesst, sieht bis zum Schliessen keine einzige fremde Aenderung -
 *     auch nicht zu ganz anderen Bestellungen. Fall 1 oben ist die Ablösung: nachgeladen wird
 *     gezielt, angehalten wird nur fuer den EINEN offenen Datensatz.
 *   - Der Drei-Wege-Merge (`mergeConcurrentValue`, `index.html:6016-6052`, aufgerufen aus
 *     `applyPendingChanges`/`rebasePendingSave` nach einem 409) entfaellt ersatzlos (Spec,
 *     "Verhaltensaenderung 1"). Fall 3 uebernimmt NICHTS automatisch.
 */
import type { Order, OrderPayment, Supplier } from "../lib/types.ts";
import type { FxDeal } from "../lib/fx.ts";
import type { BoardColumnLike } from "../lib/status.ts";
import type { OrderListColumn } from "../config/board.ts";
import type { PaymentTemplateRow } from "../api/zahlungsvorlagen.ts";
import type { FreigabeZeile } from "../api/lieferantenZahlungsziele.ts";
import {
  istDerselbeDatensatz,
  konfliktLoesen,
  konfliktVormerken,
  konfliktZu,
  nachladenErledigt,
  nachladenVormerken,
  uebernahmeAbbrechen,
  uebernahmeAbschliessen,
  uebernahmeStarten,
} from "./konflikte.ts";
import type { Datensatzverweis, Konflikt } from "./konflikte.ts";
// NUR die drei Namenskonstanten, keine Aufrufe: der Reduzierer bleibt eine reine Funktion. Die
// Module in `api/` fuehren fuer jede Entitaet den SSE-Namen UND den Pfad getrennt (Fix-Runde 1) -
// hier zaehlt ausschliesslich der SSE-Name.
import { SSE_ENTITAET as BESTELLUNG_ENTITAET } from "../api/bestellungen.ts";
import { SSE_ENTITAET as LIEFERANT_ENTITAET } from "../api/lieferanten.ts";
import { SSE_ENTITAET as KURSSICHERUNG_ENTITAET } from "../api/kurssicherung.ts";
// Reine Funktion, keine Nebenwirkung - siehe `listeErsetzen`, Abschnitt "SSE-ECHO WAEHREND DES
// EIGENEN SCHREIBVORGANGS (Board-Dublette)", die den einzigen Aufruf (Zeile ~298) traegt.
import { abgeleiteteKennung } from "../api/jtlBestellungen.ts";

export type { Datensatzverweis, Konflikt };

/**
 * Der vom Zahlungsziel- oder Zahlungsplan-Endpunkt unmittelbar bestaetigte Teil einer Bestellung.
 * `paymentPlanSource` ist die Herkunft des Plans (NULL = kein Plan); `paymentTerms` und
 * `paymentTermsTemplateId` sind bei den Plan-Endpunkten `null`, wenn keine Vorlage haengt.
 * `payments` FEHLT, wenn der Server die Raten nicht angefasst hat (Zahlungsziel nur festgehalten, Ticket G):
 * die Raten der Antwort sind roh (ohne Ist-Zahlung, Faelligkeit der Oberflaeche) und duerfen die
 * angezeigten nicht ueberschreiben - der Snapshot mischt per Spread, ein fehlender Schluessel laesst sie stehen.
 *
 * `zahlungszielGewechseltAm` (Merker "Zahlungsziel gewechselt", Migration 082) kommt nur aus der Antwort von
 * `PUT /api/orders/:id/payment-terms`. Der Snapshot fuehrt ihn AUSDRUECKLICH, auch mit `null` (nach einem
 * Zahlstatus-Reset heisst das "nie gewechselt" und ueberschreibt einen alten Zeitpunkt im Store); fehlt er in der
 * Antwort (Plan-Endpunkte, aelterer Server), fehlt auch der Schluessel und der Spread laesst den Wert stehen.
 */
export interface ZahlungszielSnapshot {
  payments?: OrderPayment[];
  paymentTerms: string | null;
  paymentTermsTemplateId: string | null;
  paymentPlanSource: string | null;
  extrasRowVersion: number;
  zahlungszielGewechseltAm?: string | null;
}

/**
 * Angemeldeter Nutzer, so wie ihn `POST /api/auth/login` und `GET /api/auth/me` liefern - derselbe
 * Typ traegt auch die Eintraege der Nutzerverwaltungs-Liste (`GET /api/users`, `NutzerSeite.tsx`).
 *
 * `jtlLogin` (Auftrag "JTL-Login in der Nutzerverwaltung", 2026-09-07): NUR in der `/api/users`-
 * Antwort gesetzt (`http/routes/users.ts`) - `POST /api/auth/login`/`GET /api/auth/me` liefern das
 * Feld nicht mit (siehe `auth/publicProfile.ts`, `jtlLogin` ist dort bewusst NICHT Teil des
 * geteilten oeffentlichen Profils). Optional und `| null`, damit beide Herkuenfte denselben Typ
 * nutzen koennen, ohne dass der Anmeldepfad das Feld erfinden muesste.
 *
 * `lastLoginAt`/`lastSeenAt` (Auftrag "Letzter Login", 2026-10-18): wie `jtlLogin` NUR in der
 * `/api/users`-Antwort gesetzt - Login und `/api/auth/me` liefern sie nicht. ISO-8601 in UTC;
 * `lastLoginAt` `null` = seit Einfuehrung nie angemeldet, `lastSeenAt` = letzte authentifizierte
 * Anfrage (minutengenau gedrosselt), `null` = unbekannt.
 */
export interface Nutzer {
  id: string;
  username: string;
  displayName: string;
  role: string;
  active: boolean;
  jtlLogin?: string | null;
  lastLoginAt?: string | null;
  lastSeenAt?: string | null;
}

/**
 * Nutzlast eines SSE-Ereignisses - vier Pflichtfelder, keine Nutzdaten (Global Constraint 13).
 * `orderId` ist ein FUENFTES, optionales Feld (Auftrag "Ereignis-Elternkennung", 2026-08-12): die
 * vier bestellungsgebundenen Entitaeten (`orderTask`, `orderPayment`, `documents`, `shipment`)
 * tragen es IMMER mit, weil der Client sie ausschliesslich ueber eine Route der Form
 * `/api/orders/:id/...` kennt und ohne die Bestellkennung nicht nachladen koennte - keine der
 * uebrigen sechs Entitaeten setzt es je (siehe `verweisVonEreignis` unten).
 */
export interface SseEreignis {
  entity: string;
  id: string;
  rowVersion: number;
  actor: string;
  orderId?: string;
}

/**
 * Eine Luecke der Live-Verbindung (Spec 2026-10-24): Ereignisse koennen verloren sein, die niemand nachliefert
 * (Neustart des Servers, Puffer zu klein, zu viel nachzuliefern). `ursache`: warum das letzte "Neu laden" aus der
 * Leiste scheiterte - `null`, solange es keines gab oder der Fehlschlag still bleibt (Abbruch, 401).
 */
export interface VerbindungsLuecke {
  ursache: unknown | null;
}

export interface Speicherzustand {
  bestellungen: Order[];
  lieferanten: Supplier[];
  fxDeals: FxDeal[];
  boardSpalten: BoardColumnLike[];
  /** Auslieferungszustand der Bestelllisten-Spalten (Task 1, Phase 6). */
  orderListSpalten: OrderListColumn[];
  /** Aufgabenvorlagen (`task_templates`) - Form steht erst mit der jeweiligen Oberflaeche fest. */
  taskVorlagen: unknown[];
  /**
   * Zahlungsvorlagen (`payment_templates`, Spec 2026-09-01 Abschnitt 3) als Erstlade-Snapshot.
   * Lieferantenformulare laden ihre Auswahl bewusst separat. Seit Task 13 entstehen Bestellraten
   * nicht mehr aus diesem Snapshot: die Freigabe kommt aus `/payment-terms`, das Setzen erfolgt
   * ueber die Bestellroute und deren Serverantwort wird in den Store uebernommen.
   */
  vorlagen: PaymentTemplateRow[];
  /** Freigabelisten je Lieferant, Schluessel ist kLieferant als Zeichenkette. */
  freigaben: Record<string, FreigabeZeile[]>;
  nutzer: Nutzer | null;
  rechte: ReadonlySet<string>;
  konflikte: Konflikt[];
  /** Datensaetze, zu denen ein Ereignis kam und deren Stand gezielt nachzuladen ist. */
  nachzuladen: Datensatzverweis[];
  /** Der aktuell zur Bearbeitung geoeffnete Datensatz - hoechstens einer. */
  geoeffnet: Datensatzverweis | null;
  /**
   * Baustein B (Auftrag "Serverseitiger Archivfilter + Ladeanzeige", 2026-08-27): `true`, waehrend
   * ein `bestandLaden()`-Lauf unterwegs ist (Erstladen NACH der Anmeldung UND ein erneuter Lauf nach
   * einem Archivfilter-Wechsel im Board, siehe `Speicher.tsx#bestandNeuLaden`) - zurueckgesetzt nach
   * Abschluss, Erfolgs- wie Fehlerfall. Seit Auftrag "Schwebender Lade-Hinweis (Spinner-Popup) statt
   * Textzeile" (2026-08-28) steuert dieses Feld den App-weiten `<LadeHinweis>` (`App.tsx`,
   * `components/App/LadeHinweis.tsx`) - NICHT mehr board-lokal und NICHT mehr zusaetzlich an
   * `bestellungen.length === 0` gekoppelt: der Hinweis erscheint bei JEDEM Ladelauf, auch bei einem
   * Archivfilter-Wechsel im bereits gefuellten Board.
   */
  ladend: boolean;
  /**
   * Fehleranzeige Paket 2 (Spec 2026-10-23, Entscheidung E12): das Fehlerobjekt, wenn das Laden des
   * Bestands scheiterte und noch kein Stand uebernommen ist - sonst `null`. Die App-Huelle zeigt daraus
   * auf den Seiten, die den Bestand lesen, die Ladefehler-Flaeche statt eines scheinbar leeren Bestands.
   * Gesetzt von `Speicher.tsx` ueber `bestandLadefehler`; ist schon ein Stand da, geht der Fehlschlag
   * stattdessen als Eintrag in den Fehlerstapel (L3), und dieses Feld bleibt `null`.
   */
  bestandLadefehler: unknown;
  /**
   * Lueckenausgleich (Spec 2026-10-24): gesetzt, solange die Leiste "Die Live-Verbindung war unterbrochen" gilt.
   * `Speicher.tsx` setzt sie erst, wenn ein Stand uebernommen ist (nie neben der Ladefehler-Flaeche), und raeumt
   * sie bei jedem gelungenen vollen Lauf ab, der nach der Luecke begann.
   */
  verbindungsluecke: VerbindungsLuecke | null;
}

/**
 * `bestellungIdGewechselt` (Task 11): eine rein JTL-gefuehrte Bestellung traegt bis zum ersten
 * Schreibvorgang die ABGELEITETE Kennung `jtl-<n>` (`api/jtlBestellungen.ts#abgeleiteteKennung`) -
 * ihre Hub-Zeile entsteht erst beim ersten Schreiben (`loeseKennungZumSchreibenAuf`,
 * `backend/src/domaene/bestellungKennung.ts`), und die traegt eine ECHTE uuid. `App.tsx
 * #drawerPositionAendern` loest diese Aktion aus, wenn ein Nachladen nach genau so einem
 * Erstschreibvorgang eine ANDERE `id` zurueckbringt, als unter der geschrieben wurde.
 *
 * NUR fuer `zustand.geoeffnet` gedacht - die Liste `bestellungen` selbst braucht keinen Nachzug:
 * `nachgeladen` (Fall unten, `mitErsetztemDatensatz`/`listeErsetzen`) ersetzt den Eintrag bereits
 * unter der ALTEN Kennung am selben Index, der neue Datensatz traegt seine neue `id` dann einfach
 * mit. `geoeffnet` ist eine SEPARATE Kopie derselben Kennung (gesetzt von `drawerOeffnen`) und
 * bekaeme den Wechsel ohne diese Aktion nie mit - die SSE-Sonderbehandlung fuer den offenen
 * Datensatz (Fall `sse`, Fall 2 unten) griffe dann fuer diese Bestellung nie wieder.
 *
 * GEZIELT, nicht blind: die Aktion ersetzt `geoeffnet.id` NUR, wenn es im Moment der Ausfuehrung noch
 * `alteId` traegt - ein zwischenzeitliches Schliessen des Drawers oder ein Sprung auf eine andere
 * Bestellung darf nicht rueckgaengig gemacht werden. Der Reduzierer prueft das gegen den
 * TATSAECHLICHEN Zustand zum Zeitpunkt der Aktion, nicht gegen eine Kopie, die der Aufrufer beim
 * Start des (asynchronen) Schreibvorgangs mitfuehrte - genau die Klasse Wettlauf, gegen die dieses
 * Projekt an anderer Stelle bereits eine `Map` statt eines Objektliterals einsetzt (siehe
 * `ENTITAET_ZU_FELD` oben): die Pruefung gehoert dorthin, wo der aktuelle Stand wirklich steht.
 */
export type Aktion =
  | { art: "sse"; ereignis: SseEreignis }
  | { art: "konflikt"; entitaet: string; id: string; geaendertVon: string; serverstand?: unknown }
  | { art: "konfliktUebernehmen"; entitaet: string; id: string }
  | { art: "konfliktVerwerfen"; entitaet: string; id: string }
  | { art: "nachgeladen"; entitaet: string; id: string; datensatz: unknown }
  /** Mischt nur den vom Zahlungsziel-Endpunkt bestaetigten Teil in die aktuelle Bestellung. */
  | { art: "zahlungszielSnapshot"; id: string; snapshot: ZahlungszielSnapshot }
  | { art: "nachladenFehlgeschlagen"; entitaet: string; id: string }
  /** Ersetzt den vollstaendigen, vom Freigabe-Endpunkt bestaetigten Satz eines Lieferanten. */
  | { art: "freigabenGesetzt"; kLieferant: number; freigaben: FreigabeZeile[] }
  | { art: "bestellungIdGewechselt"; alteId: string; neueId: string }
  | { art: "geoeffnet"; verweis: Datensatzverweis | null }
  | { art: "angemeldet"; nutzer: Nutzer | null; rechte: readonly string[] }
  /**
   * Mischt einen Bestand (oder einen Teilstand) in den Zustand - genau die Felder, die er nennt. Den
   * stehenden `bestandLadefehler` nimmt NUR ein Bestandslauf weg, und zwar ausdruecklich: `Speicher.tsx`
   * (`laufAbwickeln`) legt `bestandLadefehler: null` in den Payload. Jeder andere Aufrufer (Vorlagen,
   * Freigaben, Nutzer, "Listenspalten speichern" mit bereinigten Bestellungen ...) laesst ihn stehen: der
   * Bestand ist dann immer noch nicht geladen.
   */
  | { art: "bestandGeladen"; bestand: Partial<Speicherzustand> }
  /** Das Laden des Bestands scheiterte, und es gibt noch keinen Stand. `ursache` ist das Fehlerobjekt. */
  | { art: "bestandLadefehler"; ursache: unknown }
  /** Eine Luecke der Live-Verbindung; steht sie schon, bleibt alles (eine Leiste, eine angezeigte Ursache). */
  | { art: "verbindungsluecke" }
  /** Die Ursache des gescheiterten "Neu laden"; `null` nimmt sie weg. Wirkt nur bei stehender Luecke. */
  | { art: "verbindungslueckeUrsache"; ursache: unknown | null }
  | { art: "verbindungslueckeErledigt" }
  | { art: "ladend"; wert: boolean };

export const ANFANGSZUSTAND: Speicherzustand = {
  bestellungen: [],
  lieferanten: [],
  fxDeals: [],
  boardSpalten: [],
  orderListSpalten: [],
  taskVorlagen: [],
  vorlagen: [],
  freigaben: {},
  nutzer: null,
  rechte: new Set<string>(),
  konflikte: [],
  nachzuladen: [],
  geoeffnet: null,
  ladend: false,
  bestandLadefehler: null,
  verbindungsluecke: null,
};

/** Feld des Speichers, in dem die Datensaetze einer Entitaet liegen. */
type Listenfeld = "bestellungen" | "lieferanten" | "fxDeals";

/**
 * Entitaetsname aus dem SSE-Ereignis -> Feld im Speicher.
 *
 * Die drei Schluessel stehen NICHT als Zeichenketten hier, sondern kommen aus den API-Modulen -
 * derselben Quelle, aus der auch `NACHLADER` in `Speicher.tsx` seine Schluessel nimmt. Bis zur
 * Fix-Runde 1 standen sie hier als eigene Liste ("orders", "suppliers", "fxDeals") und waren
 * ausnahmslos falsch: das Backend sendet die EINZAHL ("order", "supplier", "fxDeal", siehe
 * `BROADCAST_ENTITIES` in `backend/src/sse/broadcast.ts`). Zwei Namenslisten fuer dieselbe Sache
 * koennen genau so auseinanderlaufen - eine kann es nicht. Dass sie zum Backend passt, prueft
 * `test/komponenten/api/entitaeten.test.ts` gegen die Backend-Datei.
 *
 * Eine `Map` und KEIN Objektliteral: der Schluessel kommt aus einem SSE-Ereignis, also aus
 * Fremddaten. Ein Objekt-Lookup mit einem solchen Schluessel greift bei `"constructor"`,
 * `"toString"` oder `"__proto__"` auf `Object.prototype` durch und liefert einen wahrheitswertigen
 * Treffer, wo keiner ist - das Fehlermuster, das in diesem Projekt bereits sechsmal zugeschlagen
 * hat (Phase-6-Plan, "Die wiederkehrenden Befundmuster"). `Map.get` kennt keine Prototypenkette.
 *
 * `"config"` steht bewusst NICHT hier: Konfigurationswerte sind keine Liste von Datensaetzen im
 * Speicher; ein Ereignis dazu wird unten als "unbekannte Entitaet" behandelt, also ignoriert.
 *
 * `"boardColumns"` stand hier bis zur Fix-Runde 1 und ist ENTFERNT - es ist kein Entitaetsname,
 * sondern ein Konfigurationsschluessel. Das Backend sendet fuer die Board-Spalten
 * `{ entity: "config", id: "boardColumns" }` (`backend/src/http/routes/config.ts`); ein Ereignis
 * mit `entity: "boardColumns"` gibt es nicht und hat es nie gegeben. Der Eintrag konnte also nie
 * treffen, haette aber den Riegel gegen `BROADCAST_ENTITIES` entschaerfen muessen. Der Leseweg fuer
 * Konfigurationsereignisse (Schluessel aus `id` statt aus `entity`) ist damit eine BENANNTE Luecke -
 * `boardSpalten` wird heute ausschliesslich beim Anfangsbestand gefuellt.
 */
const ENTITAET_ZU_FELD = new Map<string, Listenfeld>([
  [BESTELLUNG_ENTITAET, "bestellungen"],
  [LIEFERANT_ENTITAET, "lieferanten"],
  [KURSSICHERUNG_ENTITAET, "fxDeals"],
]);

/** Die `id` eines Listeneintrags, wenn er eine hat. Alles andere liefert `undefined`. */
function idVon(eintrag: unknown): string | undefined {
  if (typeof eintrag !== "object" || eintrag === null) return undefined;
  const wert = (eintrag as { id?: unknown }).id;
  return typeof wert === "string" ? wert : undefined;
}

/**
 * Die `jtlOrderId` eines Listeneintrags, wenn er eine (gueltige) hat - siehe `listeErsetzen`,
 * Abschnitt "SSE-ECHO WAEHREND DES EIGENEN SCHREIBVORGANGS". NUR Bestellungen tragen dieses Feld
 * (`api/jtlBestellungen.ts#zusammenfuehren`); Lieferanten und Kurssicherungen liefern hier immer
 * `undefined`, wie schon bei der Wettlauf-Absicherung oben.
 */
function jtlOrderIdVon(eintrag: unknown): number | undefined {
  if (typeof eintrag !== "object" || eintrag === null) return undefined;
  const wert = (eintrag as { jtlOrderId?: unknown }).jtlOrderId;
  return typeof wert === "number" && Number.isSafeInteger(wert) && wert > 0 ? wert : undefined;
}

/**
 * Ersetzt den Eintrag mit `id` durch `datensatz`. `null`/`undefined` heisst: der Datensatz
 * existiert nicht mehr (das Nachladen kam mit 404 zurueck) - dann faellt er aus der Liste. Ein
 * unbekannter Datensatz wird angehaengt: ein Ereignis kann auch eine NEUE Zeile melden.
 *
 * ================= DER WETTLAUF ZWEIER RUNDLAEUFE ZUR SELBEN BESTELLUNG (Task 14, Befund 1) =====
 *
 * `id` ist die Kennung, UNTER DER GESUCHT wird - bei `"nachgeladen"` (Reduzierer-Fall unten) ist das
 * die Kennung, mit der GESCHRIEBEN wurde (`App.tsx#drawerPositionAendern` versendet bewusst
 * `bestellungId`, nicht `neu.id` - siehe dort). Bei einer rein JTL-gefuehrten Bestellung ist das bis
 * zum ERSTEN Schreibvorgang die abgeleitete `jtl-<n>`, danach die echte uuid (Kopfkommentar
 * `bestellungIdGewechselt` oben).
 *
 * Klickt ein Anwender zwei Haekchen derselben Bestellung kurz hintereinander - schneller, als der
 * erste Rundlauf zurueckkommt -, lesen BEIDE Aufrufe `offeneBestellung.id` zum jeweiligen
 * Klickzeitpunkt und tragen deshalb dieselbe (alte) `jtl-<n>`-Kennung. Der ERSTE Rundlauf trifft
 * hier noch den vorhandenen Eintrag (`index` oben wird gefunden) und ersetzt ihn - der Eintrag traegt
 * jetzt seine eigene, neue `id`. Der ZWEITE Rundlauf sucht beim Zurueckkommen IMMER NOCH unter der
 * alten Kennung: die findet sich nicht mehr (`index === -1`), und ohne die Zeile unten wuerde er
 * angehaengt - eine Dublette derselben Bestellung, unter derselben neuen `id`, die `listeErsetzen`
 * ab da nie wieder zusammenfuehrt (sie trifft per `findIndex` immer nur den ERSTEN Treffer).
 *
 * Der Ausweg: bevor angehaengt wird, wird zusaetzlich unter der EIGENEN `id` des ankommenden
 * Datensatzes gesucht - `neu.id` traegt die Bestellung ja bereits in sich. Findet sich DORT ein
 * Eintrag (weil ein frueherer Rundlauf ihn schon dorthin verschoben hat), wird DIESER ersetzt statt
 * ein zweiter angehaengt.
 *
 * HIER in `listeErsetzen`, nicht an der Aufrufstelle in `App.tsx`: die Funktion bedient DREI
 * Entitaeten (`bestellungen`, `lieferanten`, `fxDeals`, siehe `ENTITAET_ZU_FELD`), aber der
 * Kennungswechsel `jtl-<n>` -> uuid ist eine Eigenheit AUSSCHLIESSLICH der Bestellungen
 * (`api/jtlBestellungen.ts#abgeleiteteKennung` - Lieferanten und Kurssicherungen kennen keine
 * abgeleitete, sich selbst ueberholende Kennung). Fuer die uebrigen zwei Entitaeten UND fuer jeden
 * gewoehnlichen Bestellungs-Rundlauf (bei dem die gesendete und die empfangene `id` uebereinstimmen)
 * greift der neue Zweig nie: `idVon(datensatz) === id` ist dann bereits der Normalfall, den der
 * `index`-Zweig oben abdeckt. Die Absicherung ist also fuer alle Aufrufer FOLGENLOS, ausser genau in
 * dieser einen Wettlauf-Situation - eine zweite, aufrufer-spezifische Pruefung waere unnoetig
 * verstreut, ohne dass ein Aufrufer sie anders braeuchte.
 *
 * ============= SSE-ECHO WAEHREND DES EIGENEN SCHREIBVORGANGS (Board-Dublette, 2026-09-01) =========
 *
 * Der Wettlauf oben ist NICHT der einzige, den ein Erstschreibvorgang (`jtl-<n>` -> uuid) ausloesen
 * kann - er deckt nur die Richtung "zwei Rundlaeufe suchen beide unter der ALTEN Kennung". Es gibt
 * eine ZWEITE, SPIEGELVERKEHRTE Richtung, gemeldet als "Dopplungen beim Verschieben einer Bestellung
 * im Board" (Asana): `boardGruppeAendern` (`App.tsx`) schreibt `boardGroupOverride`, dispatcht bei
 * Erfolg `{ id: <alte Kennung>, datensatz: neu }` - UND derselbe Schreibvorgang loest serverseitig
 * einen SSE-Broadcast MIT DER NEUEN uuid aus (`orders.ts#feldgruppenHandler`). Haelt ein ANDERER Tab
 * desselben Nutzers die eigentliche SSE-Verbindung (`api/ereignisse.ts`, "EINE VERBINDUNG FUER ALLE
 * TABS" - eine Anfuehrerwahl je URSPRUNG, nicht je Tab, mit einer je Tab EIGENEN `x-client-id`,
 * `api/clientKennung.ts`), greift der Ausloeser-Ausschluss des Backends
 * (`events.ts`, `actorSessionId === eigeneClientKennung`) NICHT - der schreibende Tab bekommt sein
 * eigenes Ereignis ueber den `BroadcastChannel` zurueck und merkt EBENFALLS ein Nachladen vor, aber
 * bereits unter der NEUEN uuid (`verweisVonEreignis` unten).
 *
 * Kommt DIESER SSE-Rundlauf VOR dem eigenen `boardGruppeAendern`-Rundlauf zurueck, dispatcht er
 * `{ id: <uuid>, datensatz: { id: <uuid>, ... } }` GEGEN EIN ARRAY, DAS DIE UUID NOCH GAR NICHT
 * KENNT (die Bestellung liegt dort noch unter `jtl-<n>`). Weder der `index`-Zweig oben (kein Treffer
 * unter der uuid) noch der Wettlauf-Zweig darueber greifen hier: `neueId !== id` ist FALSCH, weil
 * Suchschluessel und `datensatz.id` in DIESEM Rundlauf bereits IDENTISCH sind (beide die uuid) - der
 * Datensatz wird angehaengt. Kommt danach der eigene Rundlauf zurueck, findet er die Bestellung noch
 * unter `jtl-<n>` und ersetzt sie an Ort und Stelle mit demselben `datensatz` - jetzt tragen ZWEI
 * Eintraege dieselbe `id`, dieselbe Bestellung erscheint zweifach im Zielbereich des Boards.
 *
 * Der Ausweg: bevor angehaengt wird, wird bei einer Bestellung (die `datensatz` traegt eine
 * `jtlOrderId`) zusaetzlich unter deren ABGELEITETER Kennung gesucht - `jtl-<n>` ist eine reine
 * Funktion der `jtlOrderId` (`abgeleiteteKennung`) und bleibt deshalb auch dann auffindbar, wenn der
 * Suchschluessel dieses Rundlaufs bereits die neue uuid ist. Findet sich DORT ein (noch nicht
 * nachgezogener) Eintrag, wird DIESER ersetzt statt ein zweiter angehaengt - GENAU DIESELBE
 * Situation, nur aus der jeweils ANDEREN Richtung angefasst. Reproduziert und gruen geprueft in
 * `speicher.test.tsx`, describe "Duplikat beim Verschieben im Board".
 */
function listeErsetzen<T>(liste: T[], id: string, datensatz: unknown): T[] {
  if (datensatz === null || datensatz === undefined) {
    const ohne = liste.filter((eintrag) => idVon(eintrag) !== id);
    return ohne.length === liste.length ? liste : ohne;
  }
  const index = liste.findIndex((eintrag) => idVon(eintrag) === id);
  if (index !== -1) return liste.map((eintrag, i) => (i === index ? (datensatz as T) : eintrag));

  const neueId = idVon(datensatz);
  if (neueId !== undefined && neueId !== id) {
    const indexUnterNeuerId = liste.findIndex((eintrag) => idVon(eintrag) === neueId);
    if (indexUnterNeuerId !== -1) {
      return liste.map((eintrag, i) => (i === indexUnterNeuerId ? (datensatz as T) : eintrag));
    }
  }

  const jtlOrderId = jtlOrderIdVon(datensatz);
  if (jtlOrderId !== undefined) {
    const abgeleitet = abgeleiteteKennung(jtlOrderId);
    const indexUnterAbgeleiteterKennung = liste.findIndex((eintrag) => idVon(eintrag) === abgeleitet);
    if (indexUnterAbgeleiteterKennung !== -1) {
      return liste.map((eintrag, i) =>
        i === indexUnterAbgeleiteterKennung ? (datensatz as T) : eintrag,
      );
    }
  }

  return [...liste, datensatz as T];
}

/**
 * Schreibt `datensatz` in das zur Entitaet gehoerende Feld. Bewusst als `switch` ueber den bereits
 * aus der Map geholten Feldnamen statt als berechneter Eigenschaftszugriff `zustand[feld]` - so
 * bleibt jede Zuweisung einzeln typgeprueft, ohne einen Cast, der einen Tippfehler durchliesse.
 */
function mitErsetztemDatensatz(
  zustand: Speicherzustand,
  verweis: Datensatzverweis,
  datensatz: unknown,
): Speicherzustand {
  const feld = ENTITAET_ZU_FELD.get(verweis.entitaet);
  if (!feld) return zustand;
  switch (feld) {
    case "bestellungen":
      return { ...zustand, bestellungen: listeErsetzen(zustand.bestellungen, verweis.id, datensatz) };
    case "lieferanten":
      return { ...zustand, lieferanten: listeErsetzen(zustand.lieferanten, verweis.id, datensatz) };
    case "fxDeals":
      return { ...zustand, fxDeals: listeErsetzen(zustand.fxDeals, verweis.id, datensatz) };
  }
}

/**
 * Frontend-Kopie von `BESTELLUNGSGEBUNDENE_ENTITAETEN` (`backend/src/sse/broadcast.ts`, dort
 * exportiert). KEIN Laufzeitimport aus dem Backend: `broadcast.ts` importiert `node:events` und ist
 * nicht fuers Browser-Buendel bestimmt - ein Import zur Laufzeit risse ein Node-only-Modul ins
 * Frontend-Buendel (`test/reference/bundle.test.ts` haette das zu spaet bemerkt). Eine `Set` statt
 * eines Arrays fuer die Mitgliedspruefung unten - reine Lesbarkeit, kein Objekt-Lookup mit einem aus
 * Fremddaten stammenden Schluessel (das Fehlermuster, gegen das `ENTITAET_ZU_FELD` oben eine `Map`
 * verwendet, tritt hier nicht auf: `.has()` prueft Mitgliedschaft, greift nie auf eine Eigenschaft
 * zu).
 *
 * ZWEI Kopien derselben Liste koennen auseinanderlaufen (Erfahrung dieses Projekts, siehe
 * `test/komponenten/api/entitaeten.test.ts`) - deshalb haelt
 * `test/komponenten/api/entitaetenBestellungsgebunden.test.ts` diese Kopie gegen die Backend-Quelle
 * (RIEGEL-Muster, Nachbesserung Minor 3).
 */
const BESTELLUNGSGEBUNDENE_ENTITAETEN_FRONTEND = new Set<string>([
  "orderTask", "orderPayment", "documents", "shipment",
]);

/**
 * Baut den Verweis, den ein SSE-Ereignis ausloest - fuer die meisten Entitaeten unveraendert
 * Entitaet+Kennung des Ereignisses selbst, fuer die vier bestellungsgebundenen Entitaeten
 * (`orderTask`, `orderPayment`, `documents`, `shipment`) aber die BESTELLUNG (Auftrag
 * "Nachlader-Vier", Abschnitt "Der Weg"): "an dieser Bestellung hat sich etwas geaendert" ist die
 * tragende Idee fuer alle vier - `ladeBestellung` liefert Aufgaben und Zahlungsraten ohnehin mit,
 * und ein Dokument- oder Sendungswechsel macht ebenso "die Bestellung neu". Die Ereignis-Kennung
 * (Task-, Raten-, Dokument- bzw. Sendungs-id) kommt in `bestellungen` nie vor - ein Verweis, der bei
 * ihr bliebe, traefe entweder nichts oder haenge einen Duplikateintrag an.
 *
 * Das ist ZUGLEICH die Reparatur der Entdopplungsfalle (Auftrag, "ZWEI HINDERNISSE", Nr. 2):
 * `nachladenVormerken` (`konflikte.ts`) entdoppelt ueber `{entitaet, id}` DES VERWEISES - bekommt
 * der Verweis die Bestellkennung schon HIER als `id`, entdoppeln sich zwei Ereignisse zur SELBEN
 * Bestellung weiterhin zu einem Eintrag, waehrend zwei Ereignisse zu VERSCHIEDENEN Bestellungen
 * (z. B. eine Verantwortungsuebertragung ueber mehrere Bestellungen: `entity`, `id` [die
 * Modul-Kennung] und `rowVersion` identisch, nur `orderId` unterscheidet sich -
 * `backend/src/http/routes/zustaendigkeit.ts`) verschiedene `id`s bekommen und folgerichtig ZWEI
 * Eintraege ergeben - OHNE jede Aenderung an `nachladenVormerken`/`istDerselbeDatensatz` selbst.
 *
 * Kleinere Naht als die Alternative (die Bestellkennung als eigenes Feld an `Datensatzverweis`
 * haengen und durch `NACHLADER`/`ENTITAET_ZU_FELD`/die Ersetzung REICHEN): die vier nutzen so
 * denselben Lader UND dasselbe Zielfeld wie ein gewoehnliches `order`-Ereignis, ohne dass eine der
 * beiden Maps oder `Speicher.tsx` je etwas von ihnen erfaehrt. Bestaetigt durch den Bestand: das
 * einzige `geoeffnet` im Store zeigt IMMER auf `{ entitaet: BESTELLUNG_ENTITAET, … }`
 * (`components/App/App.tsx#drawerOeffnen`) - Fall 2 unten (Konflikt am offenen Datensatz) ist fuer
 * die vier deshalb ohnehin nur ueber die Bestellung selbst erreichbar.
 *
 * DIE UMLEITUNG GREIFT NUR, WENN BEIDES STIMMT (Nachbesserung, Minor 3 + 4): `entity` steht in
 * `BESTELLUNGSGEBUNDENE_ENTITAETEN_FRONTEND` UND `orderId` ist eine NICHT-LEERE Zeichenkette. Bis zu
 * dieser Nachbesserung reichte allein `orderId !== undefined` - heute deckungsgleich mit den vier
 * bestellungsgebundenen Entitaeten, aber nur, weil KEINE andere Entitaet je `orderId` setzt. Truege
 * kuenftig ein Ereignis EINER ANDEREN Entitaet `orderId` mit, waere das still zu einer
 * Bestell-Nachladung geworden, und ein Ereignis zu jener Entitaet haette einen fremden Datensatz in
 * `bestellungen` ersetzt. Eine leere `orderId` (`""`) ergaebe ohne die zweite Bedingung
 * `{entitaet: "order", id: ""}` - der Lader riefe `GET /api/orders/` auf, den LISTENendpunkt, dessen
 * Array als EIN Eintrag in `bestellungen` laenden wuerde. Faellt eine der beiden Bedingungen aus,
 * bleibt es beim alten Verhalten (Entitaet+Kennung des Ereignisses) - fuer die vier ohne Lader
 * bedeutet das weiterhin eine Ablehnung (`Speicher.tsx`, `KeinNachladerFehler`; der Nachlade-Effekt
 * meldet sie dem Nutzer nicht, es wurde nichts versucht), nie ein stilles `undefined`, das die Zeile
 * wuerfe (B22).
 *
 * ZWEI BENANNTE LUECKEN, NICHT REPARIERT (Nachbesserung, Important 1 + 2 - Entwurfsentscheidungen
 * ausserhalb dieses Auftrags):
 *
 *   1. `documents` UND `shipment` LADEN FORMAL RICHTIG NACH, ZEIGEN ABER NICHTS NEUES.
 *      `bestellungLaden` (`api/bestellungen.ts` -> `GET /api/orders/:id` -> `ladeBestellung`,
 *      `backend/src/domaene/orders.ts`) liefert `extras`, `tasks`, `payments` und `supplier` -
 *      KEINE Dokumentliste (`order_extras` hat keine `pdfs`-Spalte) und nichts Sendungsbezogenes.
 *      Der Sendungsreiter des Drawers haengt seit dem Auftrag „Sendungsabruf beim App-Start
 *      entfernen" (2026-08-22) ohnehin NICHT mehr am Store: `Speicherzustand` fuehrt gar kein
 *      Sendungsfeld mehr, der Reiter holt die verknuepfte Sendung ueber `sendungZuBestellungLaden`
 *      (`api/sendungen.ts`) direkt beim Oeffnen der Bestellung. Ein Beleg-Upload durch einen anderen
 *      Client loest bei DIESEM Client also den richtigen Nachladeaufruf zur richtigen Bestellung
 *      aus, der Dokumentereiter bleibt aber unveraendert - der Rundlauf ist nutzlos, aber harmlos:
 *      vor diesem Task lief das Ereignis in eine Ablehnung, jetzt in einen Nachladeaufruf ohne
 *      sichtbare Wirkung. Dahinter steckt eine Begriffsfrage beim Auftraggeber (die Leseroute
 *      liefert Checklistenzeilen, die Oberflaeche erwartet eine Dateiliste - zwei Begriffe unter
 *      einem Namen). Wer den Belegweg spaeter baut, muss NUR die Nutzlast ergaenzen
 *      (`bestellungLaden`) - der Nachlader selbst steht bereits hier.
 *
 *   2. `documents`-EREIGNISSE AUS DER GRAPH-SPIEGELUNG TRAGEN KEINE `actorSessionId`.
 *      `backend/src/graph/mirror.ts#spiegeleDatei` broadcastet nach einem erfolgreichen Upload
 *      `{ entity: "documents", actor: "system", … }` OHNE `actorSessionId` - als EINZIGE
 *      Auslösestelle alter vier bestellungsgebundenen Entitaeten. Der Ausschluss des Ausloesers
 *      (`backend/src/http/routes/events.ts`, "Ausloeser bekommt sein eigenes Ereignis nicht") greift
 *      hier folglich NICHT: der Tab, der gerade selbst hochgeladen hat, bekommt die Meldung der
 *      Spiegelung ebenfalls. Nach DIESER Umleitung trifft sie `{order, orderId}` und kann, wenn der
 *      Nutzer die Bestellung gerade geoeffnet hat, im Konflikt-Banner landen: "...wurde von system
 *      geaendert" - fuer seine EIGENE Aenderung. Vor diesem Task fiel das Ereignis lautlos weg (kein
 *      Nachlader fuer "documents"), jetzt ist es eine ECHTE, wenn auch nur bei konfiguriertem
 *      Graph-Zugang erreichbare Verhaltensaenderung (das Banner bleibt wegklickbar). Die saubere
 *      Loesung sitzt im BACKEND (die Spiegelung muesste die ausloesende Sitzung mitfuehren) und ist
 *      ein eigener, separat vergebener Task - hier bewusst nicht repariert.
 */
function verweisVonEreignis(ereignis: SseEreignis): Datensatzverweis {
  if (
    ereignis.orderId !== undefined &&
    ereignis.orderId !== "" &&
    BESTELLUNGSGEBUNDENE_ENTITAETEN_FRONTEND.has(ereignis.entity)
  ) {
    return { entitaet: BESTELLUNG_ENTITAET, id: ereignis.orderId };
  }
  return { entitaet: ereignis.entity, id: ereignis.id };
}

export function reduzierer(zustand: Speicherzustand, aktion: Aktion): Speicherzustand {
  switch (aktion.art) {
    case "sse": {
      const verweis: Datensatzverweis = verweisVonEreignis(aktion.ereignis);
      // Fall 2: der Datensatz liegt geoeffnet auf dem Tisch. NICHTS anfassen - nur melden.
      if (istDerselbeDatensatz(zustand.geoeffnet, verweis)) {
        return {
          ...zustand,
          konflikte: konfliktVormerken(zustand.konflikte, {
            ...verweis,
            geaendertVon: aktion.ereignis.actor,
          }),
        };
      }
      // Fall 1: alles andere wird gezielt nachgeladen.
      const nachzuladen = nachladenVormerken(zustand.nachzuladen, verweis);
      return nachzuladen === zustand.nachzuladen ? zustand : { ...zustand, nachzuladen };
    }

    case "konflikt": {
      // Fall 3: der 409 traegt den Serverstand bereits mit (`current`) - trotzdem wird nichts
      // uebernommen. Der eigene Stand bleibt stehen, bis der Nutzer entscheidet.
      const konflikt: Konflikt = {
        entitaet: aktion.entitaet,
        id: aktion.id,
        geaendertVon: aktion.geaendertVon,
      };
      if (aktion.serverstand !== undefined) konflikt.serverstand = aktion.serverstand;
      return { ...zustand, konflikte: konfliktVormerken(zustand.konflikte, konflikt) };
    }

    case "konfliktUebernehmen": {
      const verweis: Datensatzverweis = { entitaet: aktion.entitaet, id: aktion.id };
      const konflikt = konfliktZu(zustand.konflikte, verweis);
      if (konflikt?.serverstand !== undefined) {
        // 409-Fall: der Stand liegt schon vor, kein zweiter Aufruf noetig - der Konflikt ist mit dem Ersetzen entschieden.
        const ohneKonflikt = { ...zustand, konflikte: konfliktLoesen(zustand.konflikte, verweis) };
        return mitErsetztemDatensatz(ohneKonflikt, verweis, konflikt.serverstand);
      }
      // SSE-Fall: das Ereignis trug keine Daten - jetzt gezielt nachladen. Ohne diesen Zweig waere
      // der Knopf "neu laden" am SSE-Banner wirkungslos.
      //
      // Der Konflikt BLEIBT STEHEN, bis der aktuelle Stand wirklich geladen ist (Fehleranzeige Paket 2, Task 5): er
      // wird nur als "Uebernahme laeuft" markiert, und erst `nachgeladen` loest ihn. Frueher verschwand das Banner
      // schon hier; scheiterte das Nachladen, war der Stand alt, das Banner weg, und das naechste Speichern lief
      // wieder in den Konflikt. Bei einem Fehlschlag meldet `Speicher.tsx` (Nachlade-Effekt) und laesst ihn stehen.
      const konflikte = uebernahmeStarten(zustand.konflikte, verweis);
      const nachzuladen = nachladenVormerken(zustand.nachzuladen, verweis);
      return konflikte === zustand.konflikte && nachzuladen === zustand.nachzuladen
        ? zustand
        : { ...zustand, konflikte, nachzuladen };
    }

    case "konfliktVerwerfen": {
      const verweis: Datensatzverweis = { entitaet: aktion.entitaet, id: aktion.id };
      const konflikte = konfliktLoesen(zustand.konflikte, verweis);
      return konflikte === zustand.konflikte ? zustand : { ...zustand, konflikte };
    }

    case "nachgeladen": {
      const verweis: Datensatzverweis = { entitaet: aktion.entitaet, id: aktion.id };
      const ohneBedarf = {
        ...zustand,
        nachzuladen: nachladenErledigt(zustand.nachzuladen, verweis),
        // Der aktuelle Stand ist da: ein Konflikt mit laufender Uebernahme ist entschieden (auch bei der Antwort
        // "gibt es nicht mehr", `datensatz` null). Jeder andere Konflikt bleibt, siehe `Konflikt.uebernahmeLaeuft`.
        konflikte: uebernahmeAbschliessen(zustand.konflikte, verweis),
      };
      return mitErsetztemDatensatz(ohneBedarf, verweis, aktion.datensatz);
    }

    case "zahlungszielSnapshot": {
      // Die Promise-Antwort kann im selben React-Batch wie ein fremder Store-Dispatch eintreffen.
      // Deshalb wird der Snapshot HIER gegen den dann aktuellen Reduziererstand gemischt, statt den
      // beim Rendern beobachteten Vollstand aus `App.tsx` wieder einzusetzen.
      const aktuelleBestellung = zustand.bestellungen.find((bestellung) => bestellung.id === aktion.id);
      if (!aktuelleBestellung) return zustand;
      const datensatz: Order = { ...aktuelleBestellung, ...aktion.snapshot };
      return {
        ...zustand,
        bestellungen: listeErsetzen(zustand.bestellungen, aktion.id, datensatz),
      };
    }

    case "bestellungIdGewechselt": {
      // Siehe Kopfkommentar an `Aktion` oben: nur ersetzen, wenn `geoeffnet` JETZT noch die alte
      // Kennung traegt. `entitaet` wird NICHT extra geprueft - `alteId` ist die abgeleitete
      // `jtl-<n>`-Kennung und kommt aus keiner anderen Entitaet als `BESTELLUNG_ENTITAET` vor, ein
      // Treffer waere also ohnehin nur ueber diese Entitaet moeglich.
      if (zustand.geoeffnet && zustand.geoeffnet.id === aktion.alteId) {
        return { ...zustand, geoeffnet: { ...zustand.geoeffnet, id: aktion.neueId } };
      }
      return zustand;
    }

    case "nachladenFehlgeschlagen": {
      // Der Bedarf faellt aus der Warteschlange, der bisherige Stand bleibt stehen, und ein Konflikt mit laufender
      // Uebernahme bleibt als Konflikt stehen (ohne Marke - ein neuer Klick auf "neu laden" startet das Nachladen
      // wieder). Gemeldet wird der Fehlschlag nicht hier (der Reduzierer ist rein), sondern im Nachlade-Effekt von
      // `Speicher.tsx`, der VOR diesem Dispatch pruefen muss, ob eine Uebernahme lief: die Meldung wechselt mit ihr.
      //
      // Bewusst OHNE automatischen Wiederholungsversuch: ein Nachladen, das wegen eines Serverfehlers scheitert,
      // scheitert beim sofortigen zweiten Mal genauso - und eine Schleife daraus waere schlimmer
      // als ein veralteter Datensatz.
      //
      // Die LUECKE nach dem Wiederverbinden des Ereignisstroms (Spec 2026-10-23, E7) gleicht seit dem Lueckenausgleich
      // (Spec 2026-10-24) der Server aus: er liefert verpasste Ereignisse nach, und sie laufen wie live durch den Fall
      // `sse` oben. Wo das nicht geht (Neustart, Puffer zu klein, zu viel), meldet der Strom eine Luecke und
      // `verbindungsluecke` (unten) bringt die Leiste mit "Neu laden" - ein volles Neuladen (rund 11 MB) macht nie
      // jemand ungefragt. Ein Ereignis, dessen Nachladen SCHEITERT, meldet seit Paket 2 der Nachlade-Effekt (L4).
      const verweis: Datensatzverweis = { entitaet: aktion.entitaet, id: aktion.id };
      const nachzuladen = nachladenErledigt(zustand.nachzuladen, verweis);
      const konflikte = uebernahmeAbbrechen(zustand.konflikte, verweis);
      return nachzuladen === zustand.nachzuladen && konflikte === zustand.konflikte
        ? zustand
        : { ...zustand, nachzuladen, konflikte };
    }

    case "geoeffnet":
      return { ...zustand, geoeffnet: aktion.verweis };

    case "angemeldet":
      return {
        ...zustand,
        nutzer: aktion.nutzer,
        rechte: new Set(aktion.rechte),
        // Abmeldung (Sitzungsverlust, Abmelden): die Flaeche gehoert zur alten Sitzung. Ohne das stuende nach
        // erneuter Anmeldung kurz die alte Ursache mit bedienbarem Knopf da. Eine Anmeldung MIT Nutzer (auch die
        // Sitzungspruefung beim Start) laesst sie stehen.
        bestandLadefehler: aktion.nutzer === null ? null : zustand.bestandLadefehler,
        // Die Leiste raeumt auch eine Anmeldung aus dem abgemeldeten Zustand ab: zwischen Abmelde-Dispatch und
        // Abbau des Strom-Effekts kann sie noch einmal gesetzt werden und gehoerte dann zur alten Sitzung.
        verbindungsluecke: aktion.nutzer === null || zustand.nutzer === null ? null : zustand.verbindungsluecke,
      };

    case "bestandGeladen":
      return { ...zustand, ...aktion.bestand };

    case "bestandLadefehler":
      return { ...zustand, bestandLadefehler: aktion.ursache };

    case "verbindungsluecke":
      return zustand.verbindungsluecke ? zustand : { ...zustand, verbindungsluecke: { ursache: null } };

    case "verbindungslueckeUrsache":
      return zustand.verbindungsluecke ? { ...zustand, verbindungsluecke: { ursache: aktion.ursache } } : zustand;

    case "verbindungslueckeErledigt":
      return zustand.verbindungsluecke ? { ...zustand, verbindungsluecke: null } : zustand;

    case "freigabenGesetzt":
      return {
        ...zustand,
        freigaben: { ...zustand.freigaben, [String(aktion.kLieferant)]: aktion.freigaben },
      };

    case "ladend":
      return zustand.ladend === aktion.wert ? zustand : { ...zustand, ladend: aktion.wert };
  }
}
